import * as React from "react";
import AceEditor from "react-ace";
import {AIAgentTask, QueuedEvent} from "../../../../../generated/api";
import {Button, Checkbox, Dimmer, Dropdown, Header, Icon, Label, Loader, Modal, Segment} from "semantic-ui-react";
import {enumToDropdownItemProps} from "../../../../../utils";
import {Splitter} from "../../../../util/table-splitter-ui/src/components/Splitters/Splitter";
import {TransportOrderSelector} from "./TransportOrderSelector";
import {AiAgentTaskTable} from "./AiAgentTaskTable";
import {ConfigContext} from "../../../context/ConfigContext";
import {AiScripterResultTabView} from "./AiScripterResultTabView";
import {v4 as uuid} from 'uuid';
import {workingConfig} from "../ConfigSignal";
import {SplitterHorizontal} from "../../../../util/table-splitter-ui/src/components/Splitters/SplitterHorizontal";
import Notifications, {notify} from 'react-notify-toast';
import {backend} from "../../../../../xconvert-backend";
import {authentication} from "../../../../../authentication";
import {ScriptSelectorModal} from "./AiAgentTaskScriptsTableModal";
import {ModelPricingTable} from "./ModelinfoTable";
import {FileUploadModal} from "./FileUploadModal";
import {signal, Signal} from "@preact/signals-react";
import {ScriptSelector} from "./ScriptSelector";
import {MultiTransportOrderJsonViewer} from "./MultiTransportOrderJsonViewer";
import {QueuedEventSelector} from "./QueuedEventsSelector";
import { QueuedEventJsonViewer } from "./QueuedEventJsonViewer";
import {ScreenshotComparisonModal} from "./ScreenShotComparisonModal";

export const aiAgentTasks: Signal<AIAgentTask[]> = signal([]);

async function refreshAiAgentTasks(companyId: string) {
    try {
        const auth = backend.withTokenAuthHeader(authentication.token);
        const response = await backend.internalApi.fetchAIAgentTasks(
            companyId,
            undefined,
            undefined,
            undefined,
            undefined,
            auth
        );
        aiAgentTasks.value = response.fetchAiAgentTasksResponse;
    } catch (error) {
        if (error.status === 404) {
            console.warn("No AiAgentTasks were found for the company", error);
            notify.show('No Ai Agent Tasks were found for the company', 'info', 10000, '#f1d500');
        } else {
            console.error("Error fetching AI agent tasks:", error);
            notify.show('Error fetching AI agent tasks', 'error', 5000, '#fc0303');
        }
    }
}

export interface AiScripterPageProps {
    scriptPath: string,
    changeTab: (index: number, scriptPath: string) => void;
    onScriptPathChange?: (scriptPath: string) => void;

}

interface ChecklistGenerationRequest {
    userTaskRequest: string;
    inputScript: string | null;
}

interface ChecklistItem {
    id: string; // Added for stable keying
    originalRequest?: string | null;
    llmTaskDescription?: string | null;
}

interface ChecklistResponse {
    tasksCheckList?: ChecklistItem[] | null;
    totalPrice: number | null;
    totalUsageTokensNumber: number | null;
    totalCompletionTokensNumber: number;
    rawAiModelResponse?: string | null;
    rawAiModelInput?: string | null;
    databaseEntryId?: string | null;
}

export function AiScripterPage(props: React.PropsWithChildren<AiScripterPageProps>) {

    const context = React.useContext(ConfigContext)

    const [scriptSelectorModalOpen, setScriptSelectorModalOpen] = React.useState(false);
    const [scriptPath, setScriptPath] = React.useState(props.scriptPath)
    const [additionalPrompt, setAdditionalPrompt] = React.useState("")
    const [model, setModel] = React.useState<AIAgentTask.AiModelEnumEnum>(AIAgentTask.AiModelEnumEnum.GEMINI25PRO)
    const [transportOrderIds, setTransportOrderIds] = React.useState<string[]>([])
    const [queuedEventIds, setQueuedEventIds] = React.useState<string[]>([])

    const [transportOrderSelectorOpen, setTransportOrderSelectorOpen] = React.useState(false)
    const [xRequestId, setXRequestId] = React.useState<string | null>(null)
    const [script, setScript] = React.useState<string>("")
    const [loadedAgentTask, setLoadedAgentTask] = React.useState<AIAgentTask | null>(null)
    const [readOnly, setReadOnly] = React.useState(false)
    const [loading, setLoading] = React.useState(false)
    const [aiAgentTaskTableKey, setAiAgentTaskTableKey] = React.useState(0)
    const [modelPricingModalOpen, setModelPricingModalOpen] = React.useState(false);
    const [displayedScript, setDisplayedScript] = React.useState<string>("");
    const [scriptSource, setScriptSource] = React.useState<string>('input');
    const [scriptOptions, setScriptOptions] = React.useState<Array<{ key: string, text: string, value: string }>>([]);
    const [checklistItems, setChecklistItemsState] = React.useState<ChecklistItem[]>([]); // Renamed to avoid conflict
    const [checkedChecklistItems, setCheckedChecklistItems] = React.useState<Record<number, boolean>>({});
    const [isGeneratingChecklist, setIsGeneratingChecklist] = React.useState<boolean>(false);
    const [isChecklistModalOpen, setIsChecklistModalOpen] = React.useState(false);
    const [checklistResponseData, setChecklistResponseData] = React.useState<ChecklistResponse | null>(null);
    const [checklistTrackingData, setChecklistTrackingData] = React.useState<{
        originalRequirement: string | null;
        generatedChecklist: ChecklistItem[] | null;
        selectedChecklist: ChecklistItem[] | null;
        finalRequirement: string | null;
        checklistGeneratedAt: string | null;
        checklistAppliedAt: string | null;
        databaseEntryId: string | null;
        taskModifications: Array<{
            llmTaskDescription: string | null;
            userModifiedTaskDescription: string | null;
        }> | null;
    } | null>(null);

    const [viewingIdType, setViewingIdType] = React.useState<'transport' | 'queued' | null>(null)
    const [viewingTransportOrderIds, setViewingTransportOrderIds] = React.useState<string[] | null>(null);
    const [previouslyLoadedAgentTaskId, setPreviouslyLoadedAgentTaskId] = React.useState<string | null>(null);
    const [isExecutingInSim, setIsExecutingInSim] = React.useState(false);
    const [isInitialized, setIsInitialized] = React.useState(false);
    const [viewingQueuedEventId, setViewingQueuedEventId] = React.useState<string | null>(null)
    const [viewingQueuedEvent, setViewingQueuedEvent] = React.useState<QueuedEvent | null>(null)
    const prevCompanyIdRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (isInitialized && !loadedAgentTask && context.companyId) {
            localStorage.setItem(`aiScripterState_${context.companyId}`, JSON.stringify({
                additionalPrompt,
                model,
                transportOrderIds,
                queuedEventIds
            }));
        }
    }, [additionalPrompt, model, transportOrderIds, queuedEventIds, loadedAgentTask, isInitialized, context.companyId]);

    const handleExecuteInSim = async () => {
        if (!loadedAgentTask || (transportOrderIds.length === 0 && queuedEventIds.length === 0)) {
            notify.show('A loaded task and at least one transport order or queued event are required.', 'warning', 4000);
            return;
        }

        setIsExecutingInSim(true);
        try {
            const auth = backend.withTokenAuthHeader(authentication.token);

            if (transportOrderIds.length > 0) {
                const transportOrderId = transportOrderIds[0];
                const transportOrder = await backend.transportOrderApi.fetchTransportOrderById(transportOrderId, auth);
                const transportOrderJson = JSON.stringify(transportOrder, null, 4);

                const transferData = {
                    transportOrderId: transportOrderId,
                    transportOrderJson: transportOrderJson,
                    script: displayedScript,
                    scriptPath: scriptPath,
                };

                localStorage.setItem('scriptConverterPlaygroundTransfer', JSON.stringify(transferData));
                notify.show('Data sent to Script Converter Playground. Switching now...', 'success', 3000);
                props.changeTab(3, scriptPath);
            } else {
                notify.show('Sim execution requires at least one transport order.', 'warning', 4000);
            }
        } catch (error) {
            console.error("Failed to fetch transport order for sim execution", error);
            notify.show('Failed to fetch transport order.', 'error', 5000);
        } finally {
            setIsExecutingInSim(false);
        }
    };

    React.useEffect(() => {
        if (prevCompanyIdRef.current && prevCompanyIdRef.current !== context.companyId) {
            localStorage.removeItem(`aiScripterState_${prevCompanyIdRef.current}`);

            setAdditionalPrompt("");
            setModel(AIAgentTask.AiModelEnumEnum.GEMINI25PRO);
            setTransportOrderIds([]);
            setQueuedEventIds([]); // Add this line
            setLoadedAgentTask(null);
            setReadOnly(false);
            setChecklistTrackingData(null);
        }

        prevCompanyIdRef.current = context.companyId;

        if (context.companyId) {
            const savedState = localStorage.getItem(`aiScripterState_${context.companyId}`);
            if (savedState) {
                try {
                    const state = JSON.parse(savedState);
                    if (state.additionalPrompt) setAdditionalPrompt(state.additionalPrompt);
                    if (state.model) setModel(state.model);
                    if (state.transportOrderIds?.length) setTransportOrderIds(state.transportOrderIds);
                    if (state.queuedEventIds?.length) setQueuedEventIds(state.queuedEventIds); // Add this line
                } catch (e) {
                    console.warn('Failed to restore AI Scripter state:', e);
                }
            }
        }
        setIsInitialized(true);
    }, [context.companyId]);


    React.useEffect(() => {
        setScriptPath(props.scriptPath)
    }, [props.scriptPath])

    const [queuedEventSelectorOpen, setQueuedEventSelectorOpen] = React.useState(false)
    const [eventCategory, setEventCategory] = React.useState<AIAgentTask.EventCategoryEnum>(AIAgentTask.EventCategoryEnum.TRANSPORTORDER)
    const [selectedQueuedEventId, setSelectedQueuedEventId] = React.useState<string | null>(null)

    const getEventCategoryFromScriptPath = (scriptPath: string): AIAgentTask.EventCategoryEnum => {
        const lowerPath = scriptPath.toLowerCase()

        const lastDotIndex = scriptPath.lastIndexOf('.')
        if (lastDotIndex !== -1) {
            const scriptName = scriptPath.substring(lastDotIndex + 1).toLowerCase()

            if (scriptName.includes('exportattachmentevent')) {
                return AIAgentTask.EventCategoryEnum.ATTACHMENTEVENT
            }
            if (scriptName.includes('exportetaevent')) {
                return AIAgentTask.EventCategoryEnum.ETAEVENT
            }
        }

        if (lowerPath.includes('statusscript') || lowerPath.includes('exportstatuskotlinscript')) {
            return AIAgentTask.EventCategoryEnum.TRANSPORTORDERSTATUSEVENT
        }

        return AIAgentTask.EventCategoryEnum.TRANSPORTORDER
    }

    const determineEventCategoryFromQueuedEvent = (queuedEvent: QueuedEvent): AIAgentTask.EventCategoryEnum => {
        const EVENT_TYPES = {
            ETA_EVENT: "ETA_EVENT",
            TRANSPORT_ORDER_EVENT: "TRANSPORT_ORDER_EVENT"
        } as const;

        if (queuedEvent.eventType === EVENT_TYPES.ETA_EVENT || queuedEvent.etaEvent) {
            return AIAgentTask.EventCategoryEnum.ETAEVENT;
        }

        if (queuedEvent.eventType === EVENT_TYPES.TRANSPORT_ORDER_EVENT || queuedEvent.transportOrderEvent) {
            const changeType = queuedEvent.transportOrderEvent?.change?.type?.toString();
            if (changeType?.toUpperCase().includes('ATTACHMENT')) {
                return AIAgentTask.EventCategoryEnum.ATTACHMENTEVENT;
            }
            return AIAgentTask.EventCategoryEnum.TRANSPORTORDERSTATUSEVENT;
        }

        return AIAgentTask.EventCategoryEnum.TRANSPORTORDER;
    }


    const determineFinalEventCategory = (
        scriptPath: string,
        queuedEvent: QueuedEvent | null,
        hasTransportOrders: boolean
    ): AIAgentTask.EventCategoryEnum => {
        if (hasTransportOrders && queuedEventIds.length === 0) {
            return AIAgentTask.EventCategoryEnum.TRANSPORTORDER;
        }

        if (queuedEventIds.length > 0 && queuedEvent) {
            return determineEventCategoryFromQueuedEvent(queuedEvent);
        }
        return getEventCategoryFromScriptPath(scriptPath);
    };

    React.useEffect(() => {
        console.log("AiScripterPage useEffect, scriptPath: ", scriptPath)
        if (scriptPath !== "" && !loadedAgentTask) {
            const jp = require('jsonpath');
            const config = JSON.parse(workingConfig.value)
            const s = jp.value(config, scriptPath)
            setScript(s)
            setDisplayedScript(s)
            if (!loadedAgentTask) {
                setScriptOptions([]);
            }
        }
    }, [scriptPath, loadedAgentTask])

    React.useEffect(() => {
        if (transportOrderIds.length === 0 && queuedEventIds.length === 0) {
            // No selections, use script path
            const categoryFromPath = getEventCategoryFromScriptPath(scriptPath);
            setEventCategory(categoryFromPath);
            setSelectedQueuedEventId(null);
            console.log("Reset event category to:", categoryFromPath);
        } else {
            const finalCategory = determineFinalEventCategory(
                scriptPath,
                viewingQueuedEvent,
                transportOrderIds.length > 0
            );
            setEventCategory(finalCategory);
            console.log("Event category determined as:", finalCategory);
        }
    }, [transportOrderIds, queuedEventIds, scriptPath, viewingQueuedEvent]);

    React.useEffect(() => {
        console.log("Event category changed to:", eventCategory, {
            transportOrderIds: transportOrderIds.length,
            queuedEventIds: queuedEventIds.length,
            scriptPath
        });
    }, [eventCategory, transportOrderIds, queuedEventIds, scriptPath]); // Use arrays directly

    React.useEffect(() => {
        if (script !== undefined) {
            setDisplayedScript(script);
            setScriptSource('input');
        } else {
            setDisplayedScript("");
            setScriptSource('input');
        }
    }, [script]);

    React.useEffect(() => {
        let refreshed = aiAgentTasks.value.find((at) => {
            return at._id == loadedAgentTask?._id
        })
        if (refreshed != loadedAgentTask) {
            setLoadedAgentTask(refreshed);
        }
    }, [aiAgentTasks.value]);

    const updateScriptOptions = React.useCallback(() => {
        if (loadedAgentTask) {
            let options = [
                {key: 'input', text: 'Input Script', value: 'input'}
            ];

            loadedAgentTask.iterations?.forEach((iteration, index) => {
                options.push(iterationToDropdownOption(iteration, index));
            });
            options = options.filter((option) => option !== null);

            // Only set script on first load of this specific task
            if (loadedAgentTask._id !== previouslyLoadedAgentTaskId) {
                setDisplayedScript(loadedAgentTask.inputScript || "");
                setScript(loadedAgentTask.inputScript || "")
                setScriptSource("input")
            }
            setScriptOptions(options);
        } else {
            setScriptOptions([]);
        }
        setPreviouslyLoadedAgentTaskId(loadedAgentTask?._id);
    }, [loadedAgentTask]);

    function iterationToDropdownOption(iteration: any, index: number) {
        if (iteration.functionCallOutput?.script) {
            const timestamp = new Date(iteration.timestamp).toLocaleString();
            const source = iteration.functionCallOutput.executeFunc || "Unknown";

            const isLatestUpdated =
                loadedAgentTask.latestUpdatedScript &&
                iteration.functionCallOutput.script === loadedAgentTask.latestUpdatedScript;

            const compiledSuccessfully = iteration.queuedEventConversionResponse &&
                (!iteration.scriptExecutionLogs || iteration.scriptExecutionLogs.trim() === "");

            const compilationIcon = compiledSuccessfully ? '✅' : '❌';
            const latestScriptIndicator = isLatestUpdated ? ' -LatestUpdatedScript' : '';

            return {
                key: `iter-${index}`,
                text: `${source} ${compilationIcon}${latestScriptIndicator} (${timestamp})`,
                value: `iter-${index}`
            }
        }
        return null;
    }

    const pollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);

    React.useEffect(() => {
        const transferData = localStorage.getItem('aiScripterTransfer');
        if (transferData) {
            const data = JSON.parse(transferData);
            if (data.transportOrderId) {
                setTransportOrderIds([data.transportOrderId]);
            }
            localStorage.removeItem('aiScripterTransfer');
        }
    }, []);

    React.useEffect(() => {
        refreshAiAgentTasks(context.companyId).then(() => {
            console.log("initial load of AiAgentTasks");
        });

        if (!isChecklistModalOpen && !isGeneratingChecklist) {
            pollIntervalRef.current = setInterval(() => {
                refreshAiAgentTasks(context.companyId).then(() => {
                    console.log("refreshed AiAgentTasks");
                });
            }, 5000);
        }

        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [isChecklistModalOpen, isGeneratingChecklist]);

    React.useEffect(() => {
        updateScriptOptions();
    }, [loadedAgentTask, updateScriptOptions]);

    const [dropdownRefreshKey, setDropdownRefreshKey] = React.useState(0);

    const handleScriptSelect = (_, data) => {
        if (loadedAgentTask) {
            if (data.value === 'input') {
                setDisplayedScript(loadedAgentTask.inputScript);
                setScriptSource('input');
            } else if (data.value.startsWith('iter-')) {
                const iterIndex = parseInt(data.value.split('-')[1]);
                if (loadedAgentTask.iterations && iterIndex < loadedAgentTask.iterations.length) {
                    const iteration = loadedAgentTask.iterations[iterIndex];
                    if (iteration && iteration.functionCallOutput?.script) {
                        const scriptContent = iteration.functionCallOutput.script;
                        setDisplayedScript(scriptContent);
                        setScriptSource(data.value);
                    }
                }
            }
        }
    };
    const handleToggleChecklistItem = (index: number) => {
        setCheckedChecklistItems(prev => ({...prev, [index]: !prev[index]}));
    };
    const handleCloseChecklistModal = () => {
        setIsChecklistModalOpen(false);
        // clear items if we dont want them for next Modal
        // setChecklistItemsState([]);
        // setCheckedChecklistItems({});
        notify.show('Checklist review cancelled.', 'info', 2000);
    };

    function drawScriptDropdown() {
        return <>
            <ScriptSelector
                scriptPath={scriptPath}
                setScriptPath={handleScriptPathChange}
                setScript={(script) => {
                    console.log("setScript from ScriptSelector: ", script);
                    setScript(script);
                    setDisplayedScript(script);
                }}
                readOnly={readOnly}
            />
            {loadedAgentTask !== null && scriptOptions.length > 1 && (
                <div style={{marginBottom: "4px", marginTop: "4px"}}>
                    <Label>View Script Version:</Label>
                    <Dropdown
                        selection
                        options={scriptOptions}
                        value={scriptSource}
                        onChange={handleScriptSelect}
                        fluid
                    />
                </div>
            )}

            <AceEditor
                theme="monokai"
                mode='groovy'
                style={{flex: 2}}
                value={displayedScript}
                placeholder='The selected script will be shown here'
                width="100%"
                readOnly={scriptSource !== 'input' || readOnly}
                onChange={(value) => {
                    if (!readOnly) {
                        setScript(value)
                        setDisplayedScript(value)
                        let jp = require('jsonpath');
                        let config = JSON.parse(workingConfig.value)
                        jp.value(config, scriptPath, value)
                    }
                }}
            />

            <ScriptSelectorModal
                isOpen={scriptSelectorModalOpen}
                onClose={() => setScriptSelectorModalOpen(false)}
                scriptPath={scriptPath}
                setScriptPath={handleScriptPathChange}
                setScript={(script) => {
                    console.log("setScript from modal: ", script);
                    setScript(script);
                }}
            />
        </>
    }

    const handleQueuedEventsSelected = (queuedEvents: QueuedEvent[]) => {
        if (queuedEvents.length > 0) {
            const eventIds = queuedEvents.map(event => event._id);

            // Prevent duplicates by filtering out already existing IDs
            setQueuedEventIds(prevIds => {
                const newIds = eventIds.filter(id => !prevIds.includes(id));
                return [...prevIds, ...newIds];
            });

            const firstEvent = queuedEvents[0];
            setSelectedQueuedEventId(firstEvent._id);
            setViewingQueuedEvent(firstEvent);

            const categoryFromEvent = determineEventCategoryFromQueuedEvent(firstEvent);
            setEventCategory(categoryFromEvent);

            console.log("Selected queued events:", queuedEvents);
            console.log("Event category set to:", categoryFromEvent);
            console.log("First event details:", firstEvent);

            const categoryFromPath = getEventCategoryFromScriptPath(scriptPath);
            if (categoryFromPath !== categoryFromEvent) {
                notify.show(
                    `Info: Event type (${categoryFromEvent}) determined from selected QueuedEvent. Script path suggests ${categoryFromPath}.`,
                    'info',
                    4000
                );
            }
        }
    };

    const handleScriptPathChange = (path) => {
        if (loadedAgentTask) {
            setLoadedAgentTask(null);
            setReadOnly(false);
            setScriptOptions([]);
        }

        setScriptPath(path);
        setDropdownRefreshKey(prevKey => prevKey + 1);
        if (props.onScriptPathChange) {
            props.onScriptPathChange(path);
        }
    };

    function ready() {
        const hasExampleIds = transportOrderIds.length > 0 || queuedEventIds.length > 0;
        return !readOnly &&
            scriptPath.length > 0 &&
            additionalPrompt.length > 0 &&
            hasExampleIds;
    }

    function getLabelColor(missing: boolean) {
        return missing ? "red" : "green";
    }

    const handleTaskModified = (index: number, originalDescription: string, newDescription: string) => {
        setChecklistTrackingData(prev => {
            if (!prev || !prev.generatedChecklist) return prev;
            const actualOriginalDescription = prev.generatedChecklist[index]?.llmTaskDescription;
            if (!actualOriginalDescription) return prev;
            const modifications = prev.taskModifications || [];
            // Check if this modification already exists
            const existingModIndex = modifications.findIndex(
                mod => mod.llmTaskDescription === actualOriginalDescription
            );

            if (existingModIndex >= 0) {
                modifications[existingModIndex] = {
                    llmTaskDescription: actualOriginalDescription,
                    userModifiedTaskDescription: newDescription
                };
            } else {
                modifications.push({
                    llmTaskDescription: actualOriginalDescription,
                    userModifiedTaskDescription: newDescription
                });
            }
            return {
                ...prev,
                taskModifications: modifications
            };
        });
    };

    function additionalPromptInput() {
        const [fileUploadModalOpen, setFileUploadModalOpen] = React.useState(false);
        const [screenshotComparisonModalOpen, setScreenshotComparisonModalOpen] = React.useState(false);

        const disableInputs = readOnly || isGeneratingChecklist || isChecklistModalOpen;

        return <>
            <Label color={getLabelColor(additionalPrompt.length === 0)} style={{textAlign: "center"}}>
                Additional Prompt
            </Label>
            <div style={{display: "flex", alignItems: "center", marginBottom: "5px", marginTop: "5px"}}>
                <Button
                    icon
                    labelPosition="left"
                    onClick={() => setFileUploadModalOpen(true)}
                    size="small"
                    disabled={disableInputs}
                >
                    <Icon name="upload"/>
                    Upload File
                </Button>
                <Button
                    onClick={handleOpenChecklistGenerator}
                    loading={isGeneratingChecklist}
                    disabled={
                        disableInputs ||
                        !displayedScript || displayedScript.trim() === "" ||
                        !additionalPrompt || additionalPrompt.trim() === ""
                    }
                    style={{ marginLeft: "6px" }}
                    icon
                    labelPosition="left"
                    color="teal"
                    size="small"
                >
                    <Icon name="tasks"/>
                    Generate CheckList
                </Button>
                {/* NEW BUTTON */}
                <Button
                    onClick={() => setScreenshotComparisonModalOpen(true)}
                    disabled={disableInputs}
                    style={{ marginLeft: "6px" }}
                    icon
                    labelPosition="left"
                    color="teal"
                    size="small"
                >
                    <Icon name="images"/>
                    Compare Screenshots
                </Button>
                <Button
                    onClick={handleExecuteInSim}
                    loading={isExecutingInSim}
                    disabled={!loadedAgentTask || transportOrderIds.length === 0 || isGeneratingChecklist || isChecklistModalOpen}
                    style={{ marginLeft: "6px" }}
                    icon
                    labelPosition="left"
                    color="grey"
                    size="small"
                    title="Execute the current script and transport order in the simulator"
                >
                    <Icon name="play" />
                    Execute in Sim
                </Button>
            </div>
            <AceEditor
                theme="monokai"
                mode='text'
                style={{flex: 2}}
                value={additionalPrompt}
                placeholder='Write your requirements here, or generate a checklist from them.'
                width="100%"
                height="200px"
                onChange={(value) => {
                    if (!disableInputs) {
                        setAdditionalPrompt(value);
                    }
                }}
                readOnly={disableInputs}
                wrapEnabled={true}
                setOptions={{
                    indentedSoftWrap: false,
                    wrapMethod: 'word'
                }}
            />

            <FileUploadModal
                isOpen={fileUploadModalOpen}
                onClose={() => setFileUploadModalOpen(false)}
                onApply={(text) => {
                    setAdditionalPrompt(text);
                    notify.show('File content applied to prompt', 'success', 3000);
                }}
            />
            <ScreenshotComparisonModal
                isOpen={screenshotComparisonModalOpen}
                onClose={() => setScreenshotComparisonModalOpen(false)}
                onApply={(text) => {
                    setAdditionalPrompt(prev => prev ? `${prev}\n\n${text}` : text);
                    notify.show('Screenshot comparison added to prompt', 'success', 3000);
                }}
                transportOrderIds={transportOrderIds}
                onTransportOrderIdsChange={setTransportOrderIds}
            />
        </>;
    }

    const scriptRequiresQEkeywords = [
        'statusscript',
        'exportstatuskotlinscript',
        'exportattachmentevent',
        'exportetaevent'
    ];



    function transportOrderSelector() {
        const scriptRequiresQE = scriptRequiresQEkeywords.some(keyword => scriptPath.toLowerCase().includes(keyword));
        const hasQueuedEventSelected = queuedEventIds.length > 0;
        const hasTransportOrderSelected = transportOrderIds.length > 0;

        const isTransportOrderDisabled = readOnly || scriptRequiresQE || hasQueuedEventSelected;
        const isQueuedEventDisabled = readOnly || hasTransportOrderSelected;
        const allSelectedIds = [...transportOrderIds, ...queuedEventIds];

        return <>
            <div style={{
                display: "flex",
                alignItems: "center", justifyContent: "space-between", paddingBottom: 2, marginTop: 4}}>
                <Label.Group>
                    <Label color={getLabelColor(allSelectedIds.length < 1)}>
                        Selected IDs:
                    </Label>
                    {transportOrderIds.map((id) => (
                        <Label key={id} color="blue">
                    <span
                        style={{cursor: 'pointer', textDecoration: 'underline'}}
                        onClick={() => {
                            setViewingIdType('transport')
                            setViewingTransportOrderIds([id])
                        }}
                        title={`Click to view transport order details for ${id}`}
                    >
                        TO: {id}
                    </span>
                            {!readOnly && <Icon name='delete' onClick={() => {
                                console.log("delete Transport Order ID: ", id)
                                setTransportOrderIds(transportOrderIds.filter((orderId) => orderId !== id))
                                setScriptSource('input')
                            }}/>}
                        </Label>
                    ))}
                    {queuedEventIds.map((id) => (
                        <Label key={id} color="teal">
                    <span
                        style={{cursor: 'pointer', textDecoration: 'underline'}}
                        onClick={async () => {
                            setViewingIdType('queued')
                            setViewingQueuedEventId(id)
                            // Fetch the queued event immediately when clicked
                            try {
                                await fetchQueuedEventForViewing(id)
                            } catch (error) {
                                console.error("Failed to fetch queued event:", error)
                                notify.show('Failed to fetch queued event details.', 'error', 3000)
                            }
                        }}
                        title={`Click to view queued event details for ${id}`}
                    >
                        QE: {id}
                    </span>
                            {!readOnly && <Icon name='delete' onClick={() => {
                                console.log("delete Queued Event ID: ", id)
                                setQueuedEventIds(queuedEventIds.filter((eventId) => eventId !== id))
                                if (selectedQueuedEventId === id) {
                                    setSelectedQueuedEventId(null)
                                }
                                setScriptSource('input')
                            }}/>}
                        </Label>
                    ))}
                </Label.Group>

                <div style={{display: "flex", gap: "7px"}}>
                    <Button
                        primary
                        disabled={isTransportOrderDisabled}
                        onClick={() => setTransportOrderSelectorOpen(true)}
                        size="small"
                        title={hasQueuedEventSelected ? "Disabled: Queued Events are selected" : ""}
                    >
                        Add Transport Orders
                    </Button>
                    <Button
                        color="teal"
                        disabled={isQueuedEventDisabled}
                        onClick={() => setQueuedEventSelectorOpen(true)}
                        size="small"
                        title={hasTransportOrderSelected ? "Disabled: Transport Orders are selected" : ""}
                    >
                        Add from Queues
                    </Button>
                </div>
            </div>

            {scriptRequiresQE && transportOrderIds.length > 0 && queuedEventIds.length === 0 && (
                <Label color="red" style={{marginTop: '5px'}}>
                    <Icon name="info circle" />
                    Warning: This script type typically requires Queued Events, not Transport Orders
                </Label>
            )}

            {hasQueuedEventSelected && !scriptRequiresQE && (
                <Label color="orange" style={{marginTop: '5px'}}>
                    <Icon name="info circle" />
                    QueuedEvent selected - ensure you run with the appropriate script
                </Label>
            )}
        </>
    }



    const fetchQueuedEventForViewing = async (queuedEventId: string) => {
        try {
            const auth = backend.withTokenAuthHeader(authentication.token)
            const response = await backend.internalApi.fetchQueuedEventsForCM(
                undefined,
                context.companyId,
                undefined,
                undefined,
                undefined,
                undefined,
                1,
                undefined,
                undefined,
                undefined,
                queuedEventId,
                0,
                'TIMESTAMP',
                'DESC',
                undefined,
                auth
            )
            if (response.events && response.events.length > 0) {
                setViewingQueuedEvent(response.events[0])
            }
        } catch (error) {
            console.error("Failed to fetch queued event for viewing:", error)
            notify.show('Failed to fetch queued event details.', 'error', 3000)
        }
    }

    const handleTransportOrdersSelected = (transportOrderIds: string[]) => {
        setTransportOrderIds(transportOrderIds);

        setEventCategory(AIAgentTask.EventCategoryEnum.TRANSPORTORDER);
        setQueuedEventIds([]);
        setSelectedQueuedEventId(null);
        setViewingQueuedEvent(null);

        console.log("Transport orders selected, event category set to TRANSPORTORDER");
    };


    function modelSelector() {
        return (
            <div style={{display: "flex", alignItems: "center", width: "100%", marginTop: "4px", marginBottom: "4px"}}>
                <Dropdown
                    selectOnBlur={false}
                    placeholder='Select Model'
                    selection
                    defaultValue={model}
                    disabled={readOnly}
                    options={enumToDropdownItemProps(AIAgentTask.AiModelEnumEnum)}
                    onChange={(e, data) => {
                        setModel(data.value as AIAgentTask.AiModelEnumEnum);
                    }}
                    style={{flexGrow: 1}}
                />
                <Icon
                    name="info circle"
                    size="large"
                    color="blue"
                    style={{
                        cursor: "pointer",
                        marginLeft: "10px",
                    }}
                    onClick={() => setModelPricingModalOpen(true)}
                    title="View model pricing information"
                />
                <ModelPricingTable
                    isOpen={modelPricingModalOpen}
                    onClose={() => setModelPricingModalOpen(false)}
                />
            </div>
        );
    }


    function drawAIAgentTaskTable() {
        if (viewingIdType === 'queued' && viewingQueuedEventId) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Button
                        icon
                        labelPosition="left"
                        onClick={() => {
                            setViewingIdType(null)
                            setViewingQueuedEventId(null)
                            setViewingQueuedEvent(null)
                        }}
                        style={{ margin: '10px' }}
                    >
                        <Icon name="arrow left" />
                        Back to Tasks
                    </Button>
                    {viewingQueuedEvent ? (
                        <QueuedEventJsonViewer
                            queuedEvent={viewingQueuedEvent}
                            readOnly={true}
                        />
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <Icon name="spinner" loading size="large" />
                            <p>Loading queued event...</p>
                        </div>
                    )}
                </div>
            );
        }

        if (viewingIdType === 'transport' && viewingTransportOrderIds && viewingTransportOrderIds.length > 0) {
            return (
                <MultiTransportOrderJsonViewer
                    transportOrderIds={viewingTransportOrderIds}
                    onBack={() => {
                        setViewingIdType(null)
                        setViewingTransportOrderIds(null)
                    }}
                />
            );
        }

        // Default: show the AI Agent Task Table
        return <AiAgentTaskTable
            key={aiAgentTaskTableKey}
            loadAgentTask={loadAgentTask}
            loadedAgentTask={loadedAgentTask}
            onViewTransportOrders={(ids) => {
                setViewingIdType('transport')
                setViewingTransportOrderIds(ids)
            }}
            onViewQueuedEvents={(ids) => {
                setViewingIdType('queued')
                if (ids.length > 0) {
                    setViewingQueuedEventId(ids[0])
                    fetchQueuedEventForViewing(ids[0])
                }
            }}
        />
    }

    function loadAgentTask(task: AIAgentTask) {
        console.log("loadAgentTask: ", task)
        setScriptPath(task.inputScriptPath)
        setAdditionalPrompt(task.requirements)
        setModel(task.aiModelEnum)
        setTransportOrderIds(task.exampleTransportOrderIds || [])
        setQueuedEventIds(task.exampleQueuedEventIds || [])
        setEventCategory(task.eventCategory || AIAgentTask.EventCategoryEnum.TRANSPORTORDER)
        setLoadedAgentTask(task)
        setReadOnly(true)
        setChecklistTrackingData(null);

        setScript(task.inputScript || "")
        setDisplayedScript(task.inputScript || "")

        if (task.exampleQueuedEventIds && task.exampleQueuedEventIds.length > 0) {
            const firstQueuedEventId = task.exampleQueuedEventIds[0];
            setSelectedQueuedEventId(firstQueuedEventId);
            fetchQueuedEventForViewing(firstQueuedEventId);
        } else {
            setSelectedQueuedEventId(null);
            setViewingQueuedEvent(null);
        }

        if (props.onScriptPathChange) {
            props.onScriptPathChange(task.inputScriptPath);
        }
    }

    function drawLeftAndMiddle() {
        return <Splitter
            name={"AiScripterSplitter2"}
            left={drawAIAgentTaskTable()}
            right={drawMiddleSide()}
        />
    }

    function drawLoadedAgentTaskInfo() {
        if (loadedAgentTask) {
            return <>
                <Label color={"red"}>
                    Loaded Agent Task (read only)
                </Label>
                <Button
                    icon
                    onClick={() => {
                        setLoadedAgentTask(null)
                        setScriptPath(props.scriptPath)
                        setAdditionalPrompt("")
                        setTransportOrderIds([])
                        setQueuedEventIds([])
                        setScript("")
                        setDisplayedScript("")
                        setScriptSource("Input Script")
                        setScriptOptions([])
                        setReadOnly(false)
                        setChecklistTrackingData(null);
                    }}
                >
                    <Icon name='delete'/>
                    clear
                </Button>
            </>
        }
        return <></>
    }


    function drawMiddleSide() {
        return <>
            {drawLoadedAgentTaskInfo()}
            <SplitterHorizontal
                top={drawScriptDropdown()}
                bottom={additionalPromptInput()}
                name={"AiScripterSplitter3"}
            />
            {transportOrderSelector()}
            {modelSelector()}
            <Button
                primary
                loading={loading}
                disabled={!ready()}
                onClick={() => {
                    console.log("scriptPath: ", scriptPath)
                    console.log("additionalPrompt: ", additionalPrompt)
                    console.log("model: ", model)
                    console.log("transportOrderIds: ", transportOrderIds)
                    console.log("queuedEventIds: ", queuedEventIds)

                    startAiAgentTask(scriptPath, additionalPrompt, model, transportOrderIds, queuedEventIds).then(_ => {})
                }}
            >start</Button>
        </>
    }

    async function startAiAgentTask(
        scriptPath: string,
        additionalPrompt: string,
        model: AIAgentTask.AiModelEnumEnum,
        transportOrderIds: string[],
        queuedEventIds: string[] = []) {
        setLoading(true)
        console.log("startAiAgentTask")

        const finalEventCategory = determineFinalEventCategory(
            scriptPath,
            viewingQueuedEvent,
            transportOrderIds.length > 0
        );
        console.log("Final eventCategory:", finalEventCategory)

        let request = {} as AIAgentTask
        let xReqId = uuid()

        if (loadedAgentTask) {
            request = loadedAgentTask
            request.eventCategory = finalEventCategory
            request.exampleTransportOrderIds = transportOrderIds
            request.exampleQueuedEventIds = queuedEventIds
        } else {
            request.inputScriptPath = scriptPath
            request.inputScript = script
            request.requirements = additionalPrompt
            request.aiModelEnum = model
            request.exampleTransportOrderIds = transportOrderIds
            request.exampleQueuedEventIds = queuedEventIds
            request.companyId = context.companyId
            request.xrequestId = xReqId
            request.eventCategory = finalEventCategory

            if (checklistTrackingData) {
                request.checklistData = {
                    originalRequirement: checklistTrackingData.originalRequirement,
                    generatedChecklist: checklistTrackingData.generatedChecklist?.map(item => ({
                        originalRequest: item.originalRequest,
                        llmTaskDescription: item.llmTaskDescription
                    })) || null,
                    selectedChecklist: checklistTrackingData.selectedChecklist?.map(item => ({
                        originalRequest: item.originalRequest,
                        llmTaskDescription: item.llmTaskDescription
                    })) || null,
                    finalRequirement: additionalPrompt,
                    checklistGeneratedAt: checklistTrackingData.checklistGeneratedAt ? new Date(checklistTrackingData.checklistGeneratedAt) : null,
                    checklistAppliedAt: checklistTrackingData.checklistAppliedAt ? new Date(checklistTrackingData.checklistAppliedAt) : null,
                    databaseEntryId: checklistTrackingData.databaseEntryId,
                    taskModifications: checklistTrackingData.taskModifications?.map(mod => ({
                        llmTaskDescription: mod.llmTaskDescription,
                        userModifiedTaskDescription: mod.userModifiedTaskDescription
                    })) || null
                }
            }
        }

        setXRequestId(xReqId)
        let response = triggerAiScriptTask(request, xReqId)

        response.then((response) => {
            if (response.ok) {
                console.log("triggerAiScriptTask response: ", response)
                setChecklistTrackingData(null);
                return response.json()
            } else {
                console.error("triggerAiScriptTask error: ", response)
                throw new Error("triggerAiScriptTask error: " + response.statusText)
            }
        }).then((data) => {
            console.log("triggerAiScriptTask data: ", data)
            notify.show('AiScriptTask has been triggered', 'success', 3000, '#28f751');
            setLoading(false)
            setAiAgentTaskTableKey(aiAgentTaskTableKey + 1)
        }).catch((error) => {
            console.error("triggerAiScriptTask error: ", error)
            notify.show(error.message || 'error triggering AiScriptTask', 'error', 5000, '#fc0303');
            setLoading(false)
        })
    }



    function drawRightSide() {
        return <div style={{
            flex: 2,
            display: "flex",
            flexDirection: "column",
        }}>
            <Segment raised compact style={{display: 'inline-block'}}>
                <Header as="h3" style={{margin: 0}}>Result</Header>
            </Segment>

            <AiScripterResultTabView
                xRequestId={xRequestId}
                aiAgentTaskId={loadedAgentTask?._id}
            />
        </div>
    }

    const ChecklistModalComponent: React.FC<{
        isOpen: boolean;
        items: ChecklistItem[];
        currentCheckedItems: Record<number, boolean>;
        onToggle: (index: number) => void;
        onApply: () => void;
        onCancel: () => void;
        responseData?: ChecklistResponse;
        onTaskModified: (index: number, originalDescription: string, newDescription: string) => void;
        originalChecklist?: ChecklistItem[] | null; // Add this line

    }> = ({
              isOpen,
              items,
              currentCheckedItems,
              onToggle,
              onApply,
              onCancel,
              responseData,
              onTaskModified,
              originalChecklist,

          }) => {
        if (!isOpen) return null;

        const nothingSelected = items.length > 0 && Object.values(currentCheckedItems).every(v => !v);
        const [editingIndex, setEditingIndex] = React.useState<number | null>(null);
        const [editedDescriptions, setEditedDescriptions] = React.useState<Record<number, string>>({});
        const [originalDescriptions, setOriginalDescriptions] = React.useState<Record<number, string>>({});
        const [showRawData, setShowRawData] = React.useState<boolean>(false);

        React.useEffect(() => {
            const initialDescriptions: Record<number, string> = {};
            const originals: Record<number, string> = {};
            items.forEach((item, index) => {
                const description = item.llmTaskDescription || "";
                initialDescriptions[index] = description;
                // Use the original checklist from props if available, otherwise use current description
                originals[index] = originalChecklist?.[index]?.llmTaskDescription || description;
            });
            setEditedDescriptions(initialDescriptions);
            setOriginalDescriptions(originals);
        }, [items, originalChecklist]);

        const handleEdit = (index: number) => {
            setEditingIndex(index);
        };

        const handleSaveEdit = (index: number) => {
            setEditingIndex(null);

            const originalDesc = originalDescriptions[index];
            const newDesc = editedDescriptions[index];

            if (originalDesc !== newDesc) {
                onTaskModified(index, originalDesc, newDesc);
            }
            if (items[index]) {
                items[index].llmTaskDescription = editedDescriptions[index];
            }
        };

        const handleCancelEdit = (index: number) => {
            setEditingIndex(null);
            // Restore the original description
            setEditedDescriptions(prev => ({
                ...prev,
                [index]: items[index]?.llmTaskDescription || ""
            }));
        };

        const handleDescriptionChange = (index: number, value: string) => {
            setEditedDescriptions(prev => ({
                ...prev,
                [index]: value
            }));
        };

        return (
            <Modal
                open={isOpen}
                onClose={onCancel}
                size="fullscreen"
                dimmer="blurring"
            >
                <Header
                    icon='tasks'
                    content='Review and Select Checklist Tasks'
                    style={{
                        backgroundColor: '#f8f9fa',
                        padding: '15px',
                        borderBottom: '1px solid #e9ecef'
                    }}
                />

                {responseData && (
                    <Segment raised style={{
                        margin: '0',
                        borderRadius: '0',
                        border: 'none',
                        borderBottom: '1px solid #e9ecef'
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '15px',
                            padding: '10px 5px'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '10px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    gap: '10px',
                                    flexWrap: 'wrap'
                                }}>
                                    <Label color="green" size="large" style={{display: 'flex', alignItems: 'center'}}>
                                        <Icon name="dollar"/>
                                        Price: ${responseData.totalPrice.toFixed(6)}
                                    </Label>
                                    <Label color="blue" size="large" style={{display: 'flex', alignItems: 'center'}}>
                                        <Icon name="upload"/>
                                        Usage Tokens: {responseData.totalUsageTokensNumber.toString()}
                                    </Label>
                                    <Label color="purple" size="large" style={{display: 'flex', alignItems: 'center'}}>
                                        <Icon name="download"/>
                                        Completion Tokens: {responseData.totalCompletionTokensNumber.toString()}
                                    </Label>
                                </div>
                                <Button
                                    icon
                                    labelPosition="left"
                                    color={showRawData ? "black" : "teal"}
                                    size="small"
                                    onClick={() => setShowRawData(!showRawData)}
                                >
                                    <Icon name={showRawData ? 'eye slash' : 'eye'}/>
                                    {showRawData ? 'Hide Raw Data' : 'Show Raw Data'}
                                </Button>
                            </div>

                            {showRawData && (
                                <Segment raised style={{margin: '5px 0 10px 0', padding: '0'}}>
                                    <div style={{
                                        display: 'flex',
                                        height: '500px',
                                        flexDirection: 'column',
                                        gap: '10px'
                                    }}>
                                        <div style={{flex: 1}}>
                                            <Label color="blue" ribbon style={{marginBottom: '5px', fontSize: '0.9em'}}>
                                                <Icon name="arrow right"/> Input
                                            </Label>
                                            <AceEditor
                                                mode="json"
                                                theme="monokai"
                                                name="rawInputEditor"
                                                width="100%"
                                                height="calc(100% - 30px)"
                                                value={responseData.rawAiModelInput || "No input data available"}
                                                readOnly={true}
                                                setOptions={{useWorker: false}}
                                                style={{borderRadius: '4px'}}
                                                fontSize={14}
                                            />
                                        </div>
                                        <div style={{flex: 1}}>
                                            <Label color="green" ribbon
                                                   style={{marginBottom: '5px', fontSize: '0.9em'}}>
                                                <Icon name="arrow left"/> Response
                                            </Label>
                                            <AceEditor
                                                mode="json"
                                                theme="monokai"
                                                name="rawResponseEditor"
                                                width="100%"
                                                height="calc(100% - 30px)"
                                                value={responseData.rawAiModelResponse || "No response data available"}
                                                readOnly={true}
                                                setOptions={{useWorker: false}}
                                                style={{borderRadius: '4px'}}
                                                fontSize={14}
                                            />
                                        </div>
                                    </div>
                                </Segment>
                            )}
                        </div>
                    </Segment>
                )}

                <Modal.Content scrolling style={{
                    position: 'relative',
                    padding: '15px',
                    maxHeight: showRawData ? 'calc(100vh - 600px)' : 'calc(100vh - 400px)'
                }}>
                    {isGeneratingChecklist && items.length === 0 && (
                        <Dimmer active inverted>
                            <Loader size="large" inverted>Generating Checklist...</Loader>
                        </Dimmer>
                    )}
                    {!isGeneratingChecklist && items.length === 0 && (
                        <Segment placeholder raised>
                            <Header icon>
                                <Icon name='file alternate outline' size="large"/>
                                No checklist tasks were generated.
                            </Header>
                        </Segment>
                    )}
                    {items.map((item, index) => (
                        <Segment
                            key={index}
                            raised
                            style={{
                                marginBottom: '15px',
                                padding: '15px',
                                backgroundColor: currentCheckedItems[index] ? '#f0f8ff' : undefined,
                                borderLeft: currentCheckedItems[index] ? '3px solid #2185d0' : undefined
                            }}
                        >
                            <div style={{display: 'flex', alignItems: 'flex-start'}}>
                                <Checkbox
                                    checked={!!currentCheckedItems[index]}
                                    onChange={() => onToggle(index)}
                                    style={{
                                        marginTop: '3px',
                                        marginRight: '15px',
                                        transform: 'scale(1.1)'
                                    }}
                                />
                                <div style={{flex: 1}}>
                                    {editingIndex === index ? (
                                        <div>
                                        <textarea
                                            value={editedDescriptions[index] || ""}
                                            onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                            style={{
                                                width: '100%',
                                                minHeight: '80px',
                                                marginBottom: '10px',
                                                padding: '10px',
                                                borderRadius: '4px',
                                                border: '1px solid #ddd',
                                                fontSize: '1em'
                                            }}
                                        />
                                            <div style={{display: 'flex', gap: '10px'}}>
                                                <Button
                                                    size="small"
                                                    icon
                                                    labelPosition="left"
                                                    color="green"
                                                    onClick={() => handleSaveEdit(index)}
                                                >
                                                    <Icon name='save'/>
                                                    Save
                                                </Button>
                                                <Button
                                                    size="small"
                                                    icon
                                                    color="red"
                                                    labelPosition="left"
                                                    onClick={() => handleCancelEdit(index)}
                                                >
                                                    <Icon name='cancel'/>
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start'
                                            }}>
                                                <div style={{
                                                    fontSize: '1.05em',
                                                    lineHeight: '1.5',
                                                    color: '#333'
                                                }}>
                                                    {editedDescriptions[index] || "No description provided."}
                                                </div>
                                                <Button
                                                    icon
                                                    size="tiny"
                                                    color="blue"
                                                    style={{marginLeft: '10px'}}
                                                    onClick={() => handleEdit(index)}
                                                >
                                                    <Icon name='edit'/>
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {item.originalRequest && (
                                        <div style={{
                                            fontSize: '1em',
                                            color: '#666',
                                            marginTop: '8px',
                                            fontStyle: 'italic',
                                            backgroundColor: '#f9f9f9',
                                            padding: '8px',
                                            borderRadius: '4px',
                                            borderLeft: '2px solid #ddd'
                                        }}>
                                            <Icon name="quote left" size="small"/>
                                            <span
                                                style={{fontWeight: 'bold'}}>Original Input From Text:</span> {item.originalRequest}
                                            <Icon name="quote right" size="small"/>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Segment>
                    ))}
                </Modal.Content>
                <Modal.Actions style={{
                    backgroundColor: '#f8f9fa',
                    padding: '15px',
                    borderTop: '1px solid #e9ecef',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}>
                    <Button
                        size="small"
                        onClick={onCancel}
                    >
                        <Icon name='remove'/> Cancel
                    </Button>
                    <Button
                        color='green'
                        size="small"
                        icon
                        labelPosition="right"
                        onClick={onApply}
                        disabled={items.length === 0 || nothingSelected}
                    >
                        Apply to Prompt
                        <Icon name='checkmark'/>
                    </Button>
                </Modal.Actions>
            </Modal>
        );
    };


    const handleOpenChecklistGenerator = async () => {
        if (!displayedScript || !additionalPrompt) {
            notify.show('Script content and a prompt are required to generate a checklist.', 'warning', 4000);
            return;
        }

        setChecklistTrackingData({
            originalRequirement: additionalPrompt,
            generatedChecklist: null,
            selectedChecklist: null,
            finalRequirement: null,
            checklistGeneratedAt: new Date().toISOString(),
            checklistAppliedAt: null,
            databaseEntryId: null,
            taskModifications: null
        });

        setIsGeneratingChecklist(true);
        setChecklistItemsState([]);
        setCheckedChecklistItems({});
        setChecklistResponseData(null);

        notify.show('Generating checklist in the background...', 'info', 3000);

        try {
            const response = await generateChecklistFromPromptApi(additionalPrompt, displayedScript);
            setChecklistResponseData(response);

            if (response.tasksCheckList && response.tasksCheckList.length > 0) {
                const originalChecklist = response.tasksCheckList.map(item => ({
                    id: item.id,
                    originalRequest: item.originalRequest,
                    llmTaskDescription: item.llmTaskDescription
                }));

                const workingChecklist = response.tasksCheckList.map(item => ({
                    id: item.id,
                    originalRequest: item.originalRequest,
                    llmTaskDescription: item.llmTaskDescription
                }));

                setChecklistItemsState(workingChecklist);
                setChecklistTrackingData(prev => prev ? {
                    ...prev,
                    generatedChecklist: originalChecklist,
                    databaseEntryId: response.databaseEntryId || null
                } : null);

                const initialChecked: Record<number, boolean> = {};
                response.tasksCheckList.forEach((_, index) => {
                    initialChecked[index] = true;
                });
                setCheckedChecklistItems(initialChecked);
                notify.show('Checklist generated and ready for review!', 'success', 3000);
            } else {
                notify.show('No checklist items were generated from the prompt.', 'info', 4000);
            }
            setIsChecklistModalOpen(true);
        } catch (error: any) {
            console.error("Error generating checklist:", error);
            notify.show(`Checklist generation failed: ${error.message}`, 'error', 5000);
            setIsChecklistModalOpen(false);
        } finally {
            setIsGeneratingChecklist(false);
        }
    };

    const handleApplyChecklist = () => {
        const selectedItems = checklistItems
            .filter((_, index) => checkedChecklistItems[index])
            .map(item => ({
                id: item.id,
                originalRequest: item.originalRequest,
                llmTaskDescription: item.llmTaskDescription
            }));

        const selectedDescriptions = selectedItems
            .map(item => item.llmTaskDescription)
            .filter(desc => desc != null && desc.trim() !== "")
            .join("\n\n");

        if (selectedDescriptions) {
            setAdditionalPrompt(selectedDescriptions);

            // Update tracking data with selected checklist and applied timestamp
            setChecklistTrackingData(prev => prev ? {
                ...prev,
                selectedChecklist: selectedItems, // This contains the modified descriptions
                checklistAppliedAt: new Date().toISOString()
            } : null);

            notify.show('Prompt updated from checklist.', 'success', 3000);
        } else {
            notify.show('No checklist items selected to apply.', 'warning', 3000);
        }
        setIsChecklistModalOpen(false);
    };

    return <>
        <Splitter name={"AiScripterSplitter"} left={drawLeftAndMiddle()} right={drawRightSide()}/>
        {transportOrderSelectorOpen &&
            <TransportOrderSelector
                isOpen={transportOrderSelectorOpen}
                onClose={() => setTransportOrderSelectorOpen(false)}
                transportOrderIds={transportOrderIds}
                setTransportOrderIds={handleTransportOrdersSelected} // Use the new handler
                allowMultipleSelection={true}
            />
        }
        {queuedEventSelectorOpen &&
            <QueuedEventSelector
                isOpen={queuedEventSelectorOpen}
                onClose={() => setQueuedEventSelectorOpen(false)}
                queuedEventIds={queuedEventIds} // Change from transportOrderIds
                setQueuedEventIds={setQueuedEventIds} // Change from setTransportOrderIds
                onQueuedEventsSelected={handleQueuedEventsSelected}
                allowMultipleSelection={true}
            />
        }

        <Notifications/>
        <ChecklistModalComponent
            isOpen={isChecklistModalOpen}
            items={checklistItems}
            currentCheckedItems={checkedChecklistItems}
            onToggle={handleToggleChecklistItem}
            onApply={handleApplyChecklist}
            onCancel={handleCloseChecklistModal}
            responseData={checklistResponseData}
            onTaskModified={handleTaskModified}
            originalChecklist={checklistTrackingData?.generatedChecklist || null}
        />
    </>;
}

async function generateChecklistFromPromptApi(
    userTaskRequest: string,
    inputScript: string | null
): Promise<ChecklistResponse> {
    const payload: ChecklistGenerationRequest = {
        userTaskRequest,
        inputScript,
    };
    const uri = "/converter/v1/generateAiScriptTasksChecklist";

    const requestHeaders: Record<string, string> = {
        "Content-Type": "application/json; charset=utf-8",
    };
    const response = await fetch(uri, {
        method: "POST",
        headers: requestHeaders,
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const errorBody = await response.text();
        const displayError = errorBody || `Server error (${response.status})`;
        console.error(`Error generating checklist (API: ${uri}):`, response.status, errorBody);
        throw new Error(displayError);
    }
    return response.json() as Promise<ChecklistResponse>;
}


export async function triggerAiScriptTask(aiScriptTask: AIAgentTask, xReqId: string) {

    let auth = backend.withTokenAuthHeader(authentication.token);
    let updatedTask = await backend.internalApi.createOrModifyAiAgentTask(aiScriptTask, auth)


    let uri = "/converter/v3/triggerAiScriptTask"
    return await fetch(uri, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "x-request-id": xReqId,
            //"Authorization": auth,
        },
        body: JSON.stringify(updatedTask)
    })
}

export async function editAiAgentTaskIteration(request: {
    taskId: string,
    iterationIndex: number,
    newFunctionCallResult: string
}) {
    let auth = backend.withTokenAuthHeader(authentication.token);

    let uri = "/api/internal/editAiAgentTaskIteration"
    return await fetch(uri, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(request)
    })
}

export async function updateAiAgentTaskDirectly(aiScriptTask: AIAgentTask) {
    const auth = backend.withTokenAuthHeader(authentication.token);
    return await backend.internalApi.createOrModifyAiAgentTask(aiScriptTask, auth)
}

